/**
 * Qual produto está ativo — Consórcio ou Seguros.
 *
 * HISTÓRICO, porque a mudança inverte o desenho anterior e vale saber por quê:
 * até 2026-08 os produtos viviam em hostnames separados (consorcio./seguro.) e
 * o módulo era escolhido pelo host. Com o hub em `/`, essa separação deixou de
 * pagar por si e passou a cobrar caro:
 *
 *   - cada host é uma ORIGEM, então cada um pedia login próprio; um hub que
 *     entrega o usuário a outra origem que pede senha é um pedágio, não atalho;
 *   - hostnames irmãos sob o mesmo certificado sofriam coalescing de conexão
 *     HTTP/2 no navegador, o que produzia 403 intermitente (ver memória do
 *     projeto);
 *   - navegar entre produtos recarregava a página inteira.
 *
 * Host único resolve os três de uma vez. O módulo agora sai da ROTA:
 *
 *   /#/seguros/*     -> Seguros
 *   /#/               -> hub (nenhum produto ativo)
 *   qualquer outra    -> Consórcio
 *
 * Autorização nunca passou por aqui e continua não passando: quem vê o quê é
 * RLS + is_manager()/is_super_admin(). Isto escolhe MENU e layout.
 */

export type ProductModuleCode = "consortium" | "insurance" | "canopus";

export type ProductModuleConfig = {
  code: ProductModuleCode;
  title: string;
  /** Rota-raiz do produto — abre o Feed North desde 30/09/2026. */
  home: string;
  /** O painel de números, que até 30/09/2026 era a raiz. */
  dashboard: string;
};

export const PRODUCT_MODULES: Record<ProductModuleCode, ProductModuleConfig> = {
  // Desde 09/10/2026 o consórcio tem duas administradoras: Servopa (a de sempre,
  // em /consorcio) e Canopus (em teste, só super admin, em /canopus).
  consortium: { code: "consortium", title: "North Consórcio Servopa", home: "/consorcio", dashboard: "/consorcio/dashboard" },
  insurance: { code: "insurance", title: "North Seguros", home: "/seguros", dashboard: "/seguros/dashboard" },
  canopus: { code: "canopus", title: "North Consórcio Canopus", home: "/canopus", dashboard: "/canopus" },
};

/** Rota do hub: a landing com os dois produtos. */
export const HUB_ROUTE = "/";

/**
 * Rota do CRM da North (o quadro de leads).
 *
 * O caminho é `/deals` porque é o recurso que o react-admin já registra e a
 * tabela se chama assim. O NOME que a pessoa vê é "CRM" em todo lugar — trocar
 * a rota para `/crm` renomearia a tabela e o recurso junto, e não é isso que se
 * está pedindo. `/crm` existe como atalho e redireciona para cá.
 */
export const CRM_ROUTE = "/deals";

/**
 * Rotas que pertencem aos DOIS produtos.
 *
 * Contato e cliente são a mesma pessoa nos dois lados — a Maria que tem cota
 * também tem seguro. Antes elas caíam na regra do "qualquer outra rota é
 * consórcio", então quem estava em Seguros e clicava em Contatos era jogado
 * pra Consórcio: o menu inteiro trocava e as telas de seguro sumiam da barra.
 *
 * Agora elas não decidem produto nenhum. Quem decide é de onde a pessoa veio —
 * ver `useProductModule`.
 */
// `/deals` entra aqui pelo mesmo motivo que `/contacts`: o funil é um só para
// consórcio e seguros. Sem isto, quem estivesse em Seguros e abrisse o CRM
// veria a barra lateral inteira virar Consórcio.
// /feed: o Feed North mora no menu dos dois produtos (26/09/2026) — abre com o
// menu do produto em que a pessoa já estava.
// /mensagens, /ai e /settings (27/10/2026): Mensagens, North IA e o perfil
// também se abrem de Seguros. Fora daqui, decidiam Consórcio e ainda gravavam
// isso como produto lembrado — depois, até o Feed e os contatos abriam com o
// menu do consórcio. /tasks e /crm (atalho do /deals) pelo mesmo motivo.
const ROTAS_COMPARTILHADAS = [
  "/contacts",
  "/clients",
  "/companies",
  "/deals",
  "/crm",
  "/tasks",
  "/feed",
  "/mensagens",
  "/ai",
  "/settings",
];

export const isRotaCompartilhada = (pathname: string) => {
  const rota = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return ROTAS_COMPARTILHADAS.some((base) => rota === base || rota.startsWith(`${base}/`));
};

/**
 * O módulo que a ROTA determina sozinha. `null` quando ela não determina:
 * no hub (nenhum produto ativo) e nas rotas compartilhadas (os dois produtos).
 *
 * Os dois casos devolvem null mas NÃO são a mesma coisa, e quem consome precisa
 * separá-los: no hub o certo é não mostrar produto; na rota compartilhada o
 * certo é manter o produto em que a pessoa já estava. Use `useProductModule`,
 * que já faz essa distinção — esta função aqui é a parte pura, testável.
 */
export const resolveProductModule = (pathname: string): ProductModuleCode | null => {
  const rota = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (rota === HUB_ROUTE) return null;
  if (rota.startsWith("/seguros")) return "insurance";
  if (rota === "/canopus" || rota.startsWith("/canopus/")) return "canopus";
  if (isRotaCompartilhada(rota)) return null;
  return "consortium";
};

export const getProductModuleConfig = (code: ProductModuleCode) => PRODUCT_MODULES[code];

/** O produto do botão de troca da barra lateral: Consórcio ↔ Seguros; da Canopus, volta ao Consórcio. */
export const otherProductModule = (code: ProductModuleCode): ProductModuleCode =>
  code === "consortium" ? "insurance" : "consortium";
